// TimelineWebApp/src/telemetry/timelineLifecycle.ts
//
// Mount/dismiss lifecycle for the Timeline view. Pulled into its own
// module because dismissal can fire from two unrelated places — the React
// unmount cleanup AND the Unity __requestBack handler — and both need to
// share dwell-time bookkeeping and a dedup guard.
//
// Mount-time is held in a module-level variable (not the Zustand store)
// because the only consumer is the dismiss helper in this same file; it
// doesn't need to be observable from React.

import { trackEvent, forceFlush } from './trackEvent';
import { useTelemetryStore } from './telemetryStore';

let mountAtMs: number | null = null;
let mountedMode: 'creator' | 'reviewer' | null = null;

export function markTimelineMounted(mode: 'creator' | 'reviewer'): void {
  mountAtMs = Date.now();
  mountedMode = mode;
  const { projectSource, autoOpened } = useTelemetryStore.getState();
  trackEvent('timeline_view_mounted', {
    project_source: projectSource ?? 'unknown',
    auto_opened: autoOpened ?? false,
    mode,
  });
}

export function markTimelineDismissed(): void {
  // Dedup: dismissal can fire from React unmount AND the bridge __requestBack
  // handler. Whichever runs first wins; the other no-ops.
  if (mountAtMs == null) return;
  const dwellSeconds = (Date.now() - mountAtMs) / 1000;
  const mode = mountedMode ?? 'creator';
  mountAtMs = null;
  mountedMode = null;
  const { projectSource, autoOpened } = useTelemetryStore.getState();
  trackEvent('timeline_dismissed', {
    project_source: projectSource ?? 'unknown',
    auto_opened: autoOpened ?? false,
    dwell_seconds: Math.round(dwellSeconds * 100) / 100,
    mode,
  });
  // Force flush — Unity tears down the WebView right after closePanel,
  // so the 1-second debounce would drop this batch on the floor.
  forceFlush();
}
